import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Grid,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  ContentCopy as DuplicateIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Button from './Button';
import Card from './Card';
import TextField from './TextField';
import { useTranslation } from 'react-i18next';
import Accordion from './Accordion';

/**
 * Компонент для визуального редактирования карточек
 * @param {string} content - JSON строка с содержимым карточек
 * @param {function} onChange - Функция обработки изменений
 */
const FlippingCardsEditor = ({ content, onChange }) => {
  const { t } = useTranslation(['lessons', 'common']);
  // Начальное состояние карточек
  const [cards, setCards] = useState([]);
  // Состояние для предпросмотра
  const [previewMode, setPreviewMode] = useState(false);
  // Состояния для открытых аккордеонов
  const [expandedAccordions, setExpandedAccordions] = useState({});
  // Состояние для отображения справки по Markdown
  const [showMarkdownHelp, setShowMarkdownHelp] = useState(false);

  // Отслеживаем, изменились ли карточки с момента последнего сохранения
  const [isDirty, setIsDirty] = useState(false);

  // Таймер для отложенного сохранения
  const saveTimeoutRef = useRef(null);

  // Загрузка карточек из JSON строки при инициализации или обновлении content
  useEffect(() => {
    try {
      const parsedCards = JSON.parse(content);
      if (Array.isArray(parsedCards)) {
        setCards(parsedCards);

        // Инициализация состояния расширенных аккордеонов
        // По умолчанию первый аккордеон открыт, остальные закрыты
        const initialExpandedState = {};
        if (parsedCards.length > 0) {
          initialExpandedState[0] = true;
        }
        setExpandedAccordions(initialExpandedState);

        // Сбрасываем флаг изменений при загрузке новых данных
        setIsDirty(false);
      } else {
        // Если не массив, создаем пустой набор карточек
        setCards([]);
      }
    } catch (error) {
      console.error(t('lessons:editor.cards.parseError'), error);
      // В случае ошибки, создаем пустой набор карточек
      setCards([]);
    }
  }, [content, t]);

    // Непосредственное сохранение в родительский компонент
  const saveChangesToParent = useCallback(() => {
    if (isDirty) {
      onChange(JSON.stringify(cards, null, 2));
      setIsDirty(false);
    }
  }, [cards, isDirty, onChange]);

  // Эффект для отложенного сохранения с debounce
  useEffect(() => {
    if (isDirty) {
      // Очищаем предыдущий таймер
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Устанавливаем новый таймер для сохранения через 1 секунду
      saveTimeoutRef.current = setTimeout(() => {
        saveChangesToParent();
      }, 1000);
    }

    // Очистка при размонтировании
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [cards, isDirty, saveChangesToParent]);

  // Обновление карточек в локальном state без немедленного сохранения
  const updateCards = (newCards) => {
    setCards(newCards);
    setIsDirty(true);
  };

  // Добавление новой карточки
  const handleAddCard = () => {
    const newCard = {
      front: {
        title: t('lessons:editor.cards.newQuestion'),
        content: t('lessons:editor.cards.defaultFrontContent'),
      },
      back: {
        title: t('lessons:editor.cards.answer'),
        content: t('lessons:editor.cards.defaultBackContent'),
      },
    };

    const newCards = [...cards, newCard];
    updateCards(newCards);

    // Открыть новую карточку
    const newIndex = newCards.length - 1;
    setExpandedAccordions({
      ...expandedAccordions,
      [newIndex]: true,
    });
  };

  // Удаление карточки
  const handleDeleteCard = (index) => {
    const newCards = [...cards];
    newCards.splice(index, 1);
    updateCards(newCards);

    // Обновляем состояние расширенных аккордеонов
    const newExpandedState = {};
    Object.keys(expandedAccordions).forEach((key) => {
      const keyNum = parseInt(key);
      if (keyNum < index) {
        newExpandedState[keyNum] = expandedAccordions[keyNum];
      } else if (keyNum > index) {
        newExpandedState[keyNum - 1] = expandedAccordions[keyNum];
      }
    });
    setExpandedAccordions(newExpandedState);
  };

  // Дублирование карточки
  const handleDuplicateCard = (index) => {
    const cardToDuplicate = cards[index];
    const duplicatedCard = JSON.parse(JSON.stringify(cardToDuplicate)); // Глубокое копирование объекта

    const newCards = [...cards];
    newCards.splice(index + 1, 0, duplicatedCard);
    updateCards(newCards);

    // Открыть дублированную карточку
    setExpandedAccordions({
      ...expandedAccordions,
      [index + 1]: true,
    });
  };

  // Обновление заголовка лицевой стороны карточки
  const handleFrontTitleChange = (index, newTitle) => {
    const newCards = [...cards];
    newCards[index].front.title = newTitle;
    updateCards(newCards);
  };

  // Обновление содержимого лицевой стороны карточки
  const handleFrontContentChange = (index, newContent) => {
    const newCards = [...cards];
    newCards[index].front.content = newContent;
    updateCards(newCards);
  };

  // Обновление заголовка обратной стороны карточки
  const handleBackTitleChange = (index, newTitle) => {
    const newCards = [...cards];
    newCards[index].back.title = newTitle;
    updateCards(newCards);
  };

  // Обновление содержимого обратной стороны карточки
  const handleBackContentChange = (index, newContent) => {
    const newCards = [...cards];
    newCards[index].back.content = newContent;
    updateCards(newCards);
  };

  // Обработчик изменения состояния аккордеона
  const handleAccordionChange = (index) => (event, isExpanded) => {
    // При закрытии аккордеона сохраняем изменения
    if (!isExpanded) {
      saveChangesToParent();
    }

    setExpandedAccordions({
      ...expandedAccordions,
      [index]: isExpanded,
    });
  };

  // Обработчик потери фокуса для полей ввода
  const handleBlur = () => {
    // Сохраняем изменения при потере фокуса
    saveChangesToParent();
  };

  // Компонент для предпросмотра карточки
  const CardPreview = ({ card, index }) => {
    const [flipped, setFlipped] = useState(false);

    return (
      <Box
        onClick={() => setFlipped(!flipped)}
        sx={{
          perspective: '2000px',
          height: 200,
          cursor: 'pointer',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            transition:
              'transform 0.8s cubic-bezier(0.455, 0.03, 0.515, 0.955)',
            transformStyle: 'preserve-3d',
            transform: flipped
              ? 'rotateY(180deg) translateZ(0px)'
              : 'rotateY(0deg) translateZ(0px)',
            transformOrigin: 'center center',
            '&:hover': {
              transform: flipped
                ? 'rotateY(180deg) translateZ(15px)'
                : 'rotateY(0deg) translateZ(15px)',
              transition: 'all 0.3s',
            },
          }}
        >
          <Card
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow:
                '0 15px 25px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22)',
              transform: 'translateZ(1px)',
              borderRadius: '8px',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow:
                  '0 19px 38px rgba(0,0,0,0.30), 0 15px 12px rgba(0,0,0,0.22)',
              },
            }}
          >
            <Typography variant="h6" gutterBottom>
              {card?.front?.title || ''}
            </Typography>
            <Box sx={{ overflow: 'hidden', maxHeight: '100px' }}>
              <Box
                sx={{
                  mt: 1,
                  '& table': {
                    borderCollapse: 'collapse',
                    width: '100%',
                    mb: 2,
                  },
                  '& th, & td': {
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 1,
                    textAlign: 'left',
                  },
                  '& th': {
                    backgroundColor: 'background.default',
                    fontWeight: 'bold',
                  },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {card?.front?.content || ''}
                </ReactMarkdown>
              </Box>
            </Box>
          </Card>

          <Card
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transform: 'rotateY(180deg) translateZ(1px)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow:
                '0 15px 25px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22)',
              borderRadius: '8px',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow:
                  '0 19px 38px rgba(0,0,0,0.30), 0 15px 12px rgba(0,0,0,0.22)',
              },
            }}
          >
            <Typography variant="h6" gutterBottom>
              {card?.back?.title || ''}
            </Typography>
            <Box sx={{ overflow: 'hidden', maxHeight: '100px' }}>
              <Box
                sx={{
                  mt: 1,
                  '& table': {
                    borderCollapse: 'collapse',
                    width: '100%',
                    mb: 2,
                  },
                  '& th, & td': {
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 1,
                    textAlign: 'left',
                  },
                  '& th': {
                    backgroundColor: 'background.default',
                    fontWeight: 'bold',
                  },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {card?.back?.content || ''}
                </ReactMarkdown>
              </Box>
            </Box>
          </Card>
        </Box>
      </Box>
    );
  };

  // Компонент справки по Markdown
  const MarkdownHelp = () => (
    <Paper elevation={0} sx={{ mb: 3, maxWidth: '100%', overflow: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        {t('lessons:editor.markdownHelpTitle')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>
            {t('lessons:editor.cards.basicFormatting')}
          </Typography>
          <Box
            component="pre"
            sx={{
              backgroundColor: 'background.default',
              p: 1,
              borderRadius: 1,
              fontSize: '0.85rem',
              overflowX: 'auto',
            }}
          >
            {`# ${t('lessons:editor.exampleHeading1')}
## ${t('lessons:editor.exampleHeading2')}
### ${t('lessons:editor.cards.heading3')}

**${t('lessons:editor.exampleBold')}**
*${t('lessons:editor.exampleItalic')}*
~~${t('lessons:editor.cards.strikethrough')}~~

- ${t('lessons:editor.exampleListItem1')}
- ${t('lessons:editor.exampleListItem2')}
  - ${t('lessons:editor.cards.nestedItem')}

1. ${t('lessons:editor.cards.numberedList')}
2. ${t('lessons:editor.cards.secondItem')}`}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>
            {t('lessons:editor.cards.advancedElements')}
          </Typography>
          <Box
            component="pre"
            sx={{
              backgroundColor: 'background.default',
              p: 1,
              borderRadius: 1,
              fontSize: '0.85rem',
              overflowX: 'auto',
            }}
          >
            {`[${t('lessons:editor.exampleLink')}](https://example.com)

> ${t('lessons:editor.cards.quote')}

\`\`\`
${t('lessons:editor.exampleCodeBlock')}
\`\`\`

\`${t('lessons:editor.cards.inlineCode')}\`

---
(${t('lessons:editor.cards.horizontalLine')})`}
          </Box>
        </Grid>
      </Grid>
      <Button
        size="small"
        variant="outlined"
        sx={{ mt: 2 }}
        onClick={() => setShowMarkdownHelp(false)}
      >
        {t('lessons:editor.closeHelp')}
      </Button>
    </Paper>
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2,
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">
          {t('lessons:editor.cards.titleHeader')} ({cards.length} {t('lessons:editor.cards.count')})
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={t('lessons:editor.markdownGuide')}>
            <IconButton
              color={showMarkdownHelp ? 'primary' : 'default'}
              onClick={() => setShowMarkdownHelp(!showMarkdownHelp)}
              sx={{ mr: 1 }}
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => setPreviewMode(!previewMode)}
            size="small"
          >
            {previewMode ? t('lessons:editor.exitPreview') : t('lessons:editor.preview')}
          </Button>
        </Box>
      </Box>

      {showMarkdownHelp && <MarkdownHelp />}

      {previewMode ? (
        // Режим предпросмотра
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            {cards.map((card, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <CardPreview card={card} index={index} />
              </Grid>
            ))}
          </Grid>

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddCard}
            sx={{ mt: 2 }}
          >
            {t('lessons:editor.cards.addCard')}
          </Button>
        </Box>
      ) : (
        // Режим редактирования
        <Box>
          {cards.length === 0 ? (
            <Paper elevation={0} sx={{ textAlign: 'center', mb: 2 }}>
              <Typography color="text.secondary" gutterBottom>
                {t('lessons:editor.cards.noCards')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddCard}
              >
                {t('lessons:editor.cards.addCard')}
              </Button>
            </Paper>
          ) : (
            // Список карточек для редактирования
            <Box>
              {cards.map((card, index) => (
                <Accordion
                  key={index}
                  sx={{ mb: 2 }}
                  elevation={0}
                  expanded={expandedAccordions[index] || false}
                  onChange={handleAccordionChange(index)}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography>
                        {t('lessons:editor.cards.cardNumber', { number: index + 1 })}:{' '}
                        {card?.front?.title || ''}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title={t('lessons:editor.cards.duplicate')}>
                          <Box
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateCard(index);
                            }}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: 'action.hover',
                              },
                            }}
                          >
                            <DuplicateIcon fontSize="small" />
                          </Box>
                        </Tooltip>
                        <Tooltip title={t('lessons:editor.cards.delete')}>
                          <Box
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCard(index);
                            }}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              cursor: 'pointer',
                              color: 'error.main',
                              '&:hover': {
                                bgcolor: 'action.hover',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </Box>
                        </Tooltip>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {/* Редактирование лицевой стороны */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ fontWeight: 'bold' }}
                        >
                          {t('lessons:editor.cards.frontSide')}
                        </Typography>
                        <TextField
                          fullWidth
                          label={t('lessons:editor.cards.title')}
                          value={card?.front?.title || ''}
                          onChange={(e) =>
                            handleFrontTitleChange(index, e.target.value)
                          }
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          onBlur={handleBlur}
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          multiline
                          rows={6}
                          label={t('lessons:editor.content')}
                          value={card?.front?.content || ''}
                          onChange={(e) =>
                            handleFrontContentChange(index, e.target.value)
                          }
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          onBlur={handleBlur}
                          helperText={t('lessons:editor.markdownHelp')}
                        />
                        {expandedAccordions[index] && (
                          <Paper
                            elevation={0}
                            variant="outlined"
                            sx={{ mt: 2, p: 2, borderRadius: '16px' }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              gutterBottom
                            >
                              {t('lessons:editor.preview')}:
                            </Typography>
                            <Box
                              sx={{
                                mt: 1,
                                '& table': {
                                  borderCollapse: 'collapse',
                                  width: '100%',
                                  mb: 2,
                                },
                                '& th, & td': {
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  p: 1,
                                  textAlign: 'left',
                                },
                                '& th': {
                                  backgroundColor: 'background.default',
                                  fontWeight: 'bold',
                                },
                              }}
                            >
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {card?.front?.content || ''}
                              </ReactMarkdown>
                            </Box>
                          </Paper>
                        )}
                      </Grid>

                      {/* Редактирование обратной стороны */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ fontWeight: 'bold' }}
                        >
                          {t('lessons:editor.cards.backSide')}
                        </Typography>
                        <TextField
                          fullWidth
                          label={t('lessons:editor.cards.title')}
                          value={card?.back?.title || ''}
                          onChange={(e) =>
                            handleBackTitleChange(index, e.target.value)
                          }
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          onBlur={handleBlur}
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          multiline
                          rows={6}
                          label={t('lessons:editor.content')}
                          value={card?.back?.content || ''}
                          onChange={(e) =>
                            handleBackContentChange(index, e.target.value)
                          }
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          onBlur={handleBlur}
                          helperText={t('lessons:editor.markdownHelp')}
                        />
                        {expandedAccordions[index] && (
                          <Paper
                            elevation={0}
                            variant="outlined"
                            sx={{ mt: 2, p: 2, borderRadius: '16px' }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              gutterBottom
                            >
                              {t('lessons:editor.preview')}:
                            </Typography>
                            <Box
                              sx={{
                                mt: 1,
                                '& table': {
                                  borderCollapse: 'collapse',
                                  width: '100%',
                                  mb: 2,
                                },
                                '& th, & td': {
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  p: 1,
                                  textAlign: 'left',
                                },
                                '& th': {
                                  backgroundColor: 'background.default',
                                  fontWeight: 'bold',
                                },
                              }}
                            >
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {card?.back?.content || ''}
                              </ReactMarkdown>
                            </Box>
                          </Paper>
                        )}
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddCard}
                sx={{ mt: 1 }}
              >
                {t('lessons:editor.cards.addCard')}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FlippingCardsEditor;
